import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useComponentTranslation } from "@/services/i18n";
import { FaCopy, FaExternalLinkAlt, FaLinkedin, FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import { CANDIDATE_URL } from "@/config";
import API from "@/services/api";
import PublishToggle from "./components/publish-toggle";

const translations = {
  en: {
    title: "LinkedIn",
    description: "Post your job on LinkedIn to reach professional candidates",
    post_now: "Post on LinkedIn",
    copy_link: "Copy Link",
    link_copied: "Link copied to clipboard",
    instructions: "Copy this link and post it on LinkedIn",
    publish: "Publish",
    unpublish: "Unpublish",
    post_published: "Post published successfully",
    post_unpublished: "Post unpublished successfully",
    post_not_enabled: "Failed to publish post",
    back: "Back to channels",
  },
  fr: {
    title: "LinkedIn",
    description: "Publiez votre offre sur LinkedIn pour atteindre des candidats professionnels",
    post_now: "Publier sur LinkedIn",
    copy_link: "Copier le lien",
    link_copied: "Lien copié dans le presse-papiers",
    instructions: "Copiez ce lien et publiez-le sur LinkedIn",
    publish: "Publier",
    unpublish: "Dépublier",
    post_published: "Publication publiée avec succès",
    post_unpublished: "Publication dépubliée avec succès",
    post_not_enabled: "Échec de la publication",
    back: "Retour aux canaux",
  },
};

export default function LinkedIn({ job, setJob, posts = [], setPosts }) {
  const { t } = useComponentTranslation(translations);
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const channelSlug = "linkedin";

  useEffect(() => {
    fetchChannel();
  }, []);

  const fetchChannel = async () => {
    try {
      const response = await API.post(`/channel/search`, {
        slug: channelSlug,
        limit: 1,
      });
      if (response.ok && response.data?.[0]) {
        setChannel(response.data[0]);
      }
    } catch (e) {
      console.error("Error fetching channel:", error);
    }
  };

  // Find job post from posts array
  const jobPost = channel ? posts.find((post) => post.channel_id === channel._id) : null;

  const generateShareLink = () => {
    const url = new URL(`${CANDIDATE_URL}/jobs`);
    if (jobPost?._id) {
      url.searchParams.append("jobpost_id", jobPost._id);
    }
    return url.toString();
  };

  const shareLink = generateShareLink();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success(t("link_copied"));
  };

  const handlePostOnLinkedIn = () => {
    window.open("https://www.linkedin.com/talent/job-postings", "_blank");
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button */}
      <button onClick={() => navigate("..")} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <FaArrowLeft className="w-4 h-4" />
        {t("back")}
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
            <FaLinkedin className="w-9 h-9 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-2xl font-bold text-gray-900">{channel?.name || t("title")}</h3>
              {channel && <PublishToggle job={job} channel={channel} posts={posts} setPosts={setPosts} />}
            </div>

            <p className="text-sm text-gray-600 mb-6">{t("description")}</p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-2">{t("instructions")}</p>
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded px-3 py-2">
                <code className="text-sm text-gray-600 flex-1 truncate">{shareLink}</code>
                <button onClick={handleCopyLink} className="p-1.5 hover:bg-gray-100 rounded transition-colors" title={t("copy_link")}>
                  <FaCopy className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            <button onClick={handlePostOnLinkedIn} className="btn btn-primary flex items-center gap-2">
              <FaExternalLinkAlt className="w-4 h-4" />
              {t("post_now")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
